import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useCreateVehicle, useVehicles } from '@/data/vehicles';
import { useDriversList } from '@/data/drivers';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { VerificationBadge } from '@/components/verification-badge';
import { TableScroll } from '@/components/ui/table-scroll';
import { cn } from '@/lib/cn';

const VEHICLE_TYPES = [
  // Two wheels + car
  ['cycle', 'Cycle'],
  ['cargo_bike', 'Cargo bike'],
  ['motorcycle', 'Motorcycle'],
  ['car', 'Car'],
  // The van size ladder
  ['small_van', 'Small van'],
  ['midi_van', 'Midi van'],
  ['swb', 'SWB van'],
  ['mwb', 'MWB van'],
  ['lwb', 'LWB van'],
  ['xlwb', 'XLWB van'],
  ['luton', 'Luton'],
  ['jumbo_luton', 'Jumbo Luton'],
  // Rigids
  ['rigid_7_5t', 'Rigid 7.5t'],
  ['rigid_12t', 'Rigid 12t'],
  ['rigid_18t', 'Rigid 18t'],
  ['rigid_26t', 'Rigid 26t'],
  // Artics + skellies
  ['artic', 'Artic'],
  ['drawbar', 'Drawbar'],
  ['tractor_4axle', 'Tractor unit (4 axle)'],
  ['tractor_6axle', 'Tractor unit (6 axle)'],
  ['skellie_1x20', 'Skellie 1×20ft'],
  ['skellie_2x20', 'Skellie 2×20ft'],
  ['skellie_1x40', 'Skellie 1×40ft'],
  ['skellie_1x44', 'Skellie 1×44ft'],
  // Body types
  ['flatbed', 'Flatbed'],
  ['curtainsider', 'Curtainsider'],
  ['box', 'Box'],
  ['refrigerated', 'Refrigerated'],
  ['tipper', 'Tipper'],
  ['other', 'Other'],
] as const;

const TYPE_LABELS = new Map<string, string>(VEHICLE_TYPES as unknown as [string, string][]);

export function vehicleTypeLabel(t: string): string {
  return TYPE_LABELS.get(t) ?? t.replace(/_/g, ' ');
}

export function VehiclesPage() {
  const { fleet } = useActiveFleet();
  const vehiclesQuery = useVehicles(fleet?.id ?? null);
  const driversQuery = useDriversList(fleet?.id ?? null);
  const [showAdd, setShowAdd] = useState(false);

  const driverName = (userId: string | null) =>
    userId
      ? (driversQuery.data?.find((d) => d.userId === userId)?.displayName ?? 'Driver')
      : null;

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Vehicles
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Fleet vehicles with live government verification — tax and MOT
            checked against DVLA and DVSA, re-checked nightly.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="rounded-full bg-breaker-identity px-3.5 py-2 text-sm font-medium text-white hover:bg-breaker-identity-hover transition-colors"
        >
          {showAdd ? 'Close' : 'Add vehicle'}
        </button>
      </header>

      {showAdd && fleet && (
        <AddVehicleCard fleetId={fleet.id} onDone={() => setShowAdd(false)} />
      )}

      {vehiclesQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : vehiclesQuery.isError ? (
        <EmptyState
          title="Couldn&rsquo;t load vehicles"
          description={(vehiclesQuery.error as Error)?.message ?? 'Server error.'}
        />
      ) : (vehiclesQuery.data ?? []).length === 0 ? (
        <EmptyState
          title="No vehicles yet"
          description="Add your first van or truck — Breaker verifies its tax and MOT against government records automatically."
        />
      ) : (
        <div className="surface rounded-lg overflow-hidden">
          <TableScroll>
          <table className="w-full text-sm">
            <thead className="bg-breaker-ground-2 border-b border-breaker-line">
              <tr className="text-left">
                <Th>Registration</Th>
                <Th>Type</Th>
                <Th>Make / model</Th>
                <Th>Owner</Th>
                <Th className="w-[210px]">Verification</Th>
              </tr>
            </thead>
            <tbody>
              {(vehiclesQuery.data ?? []).map((v) => (
                <tr
                  key={v.id}
                  className="border-b last:border-b-0 border-breaker-line hover:bg-breaker-ground-2 transition-colors"
                >
                  <Td>
                    <Link
                      to="/vehicles/$vehicleId"
                      params={{ vehicleId: v.id }}
                      className="font-mono font-medium text-breaker-ink hover:text-breaker-identity transition-colors"
                    >
                      {v.registration}
                    </Link>
                  </Td>
                  <Td>{vehicleTypeLabel(v.type)}</Td>
                  <Td>{[v.make, v.model].filter(Boolean).join(' ') || '—'}</Td>
                  <Td>
                    {v.ownerDriverUserId ? (
                      <span className="text-breaker-ink-2">
                        {driverName(v.ownerDriverUserId)}
                      </span>
                    ) : (
                      <span className="text-breaker-mute">Pool</span>
                    )}
                  </Td>
                  <Td>
                    <VerificationBadge verification={v.verification} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-4 py-2.5 text-2xs font-semibold uppercase tracking-wider text-breaker-mute', className)}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3', className)}>{children}</td>;
}

function AddVehicleCard({ fleetId, onDone }: { fleetId: string; onDone: () => void }) {
  const create = useCreateVehicle(fleetId);
  const driversQuery = useDriversList(fleetId);
  const [registration, setRegistration] = useState('');
  const [type, setType] = useState<(typeof VEHICLE_TYPES)[number][0]>('lwb');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [ownerId, setOwnerId] = useState('');

  const submit = () => {
    if (!registration.trim()) return Promise.resolve();
    return create
      .mutateAsync({
        registration: registration.trim().toUpperCase(),
        type,
        make: make.trim() || null,
        model: model.trim() || null,
        ownerDriverUserId: ownerId || null,
      })
      .then(onDone)
      .catch(() => undefined); // create.isError renders the message below
  };

  return (
    <div className="surface rounded-lg p-5 mb-6">
      <div className="grid grid-cols-[160px_170px_1fr_1fr_180px_auto] gap-3 items-end">
        <label className="block">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">Registration</span>
          <input
            value={registration}
            onChange={(e) => setRegistration(e.target.value.toUpperCase())}
            placeholder="NB70 VAN"
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm font-mono focus:border-breaker-identity focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as typeof type)}
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm focus:border-breaker-identity focus:outline-none"
          >
            {VEHICLE_TYPES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">Make</span>
          <input
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="Citroën"
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm focus:border-breaker-identity focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">Model</span>
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Relay L3H2"
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm focus:border-breaker-identity focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-2xs font-semibold uppercase tracking-wider text-breaker-mute">Owner</span>
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="mt-1 w-full rounded-md border border-breaker-line bg-breaker-ground px-3 py-2 text-sm focus:border-breaker-identity focus:outline-none"
          >
            <option value="">Fleet pool</option>
            {(driversQuery.data ?? []).map((d) => (
              <option key={d.userId} value={d.userId}>
                {d.displayName}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          action={submit}
          disabled={!registration.trim()}
        >
          {create.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
      {create.isError && (
        <p className="mt-3 text-sm text-breaker-red-deep">
          {(create.error as Error)?.message ?? 'Could not add vehicle.'}
        </p>
      )}
      <p className="mt-3 text-xs text-breaker-mute">
        The registration is checked against DVLA and DVSA records automatically — make sure it matches the plate.
      </p>
    </div>
  );
}
